import React, { useState } from 'react'
import { TrendingUp, TrendingDown, DollarSign, BarChart3, Target, Star, Users, MessageCircle, Share2, Bookmark, Calendar, ArrowUpRight, ArrowDownRight, Zap, Award, Eye } from 'lucide-react'

interface CryptoInfluencerProps {
  data: {
    influencerName: string
    handle: string
    bio: string
    avatar: string
    coverImage: string
    followers: number
    following: number
    verified: boolean
    socialLinks: {
      twitter: string
      youtube: string
      instagram: string
      tiktok: string
    }
    portfolio: {
      totalValue: string
      dayChange: number
      dayChangeValue: string
      topHoldings: Array<{
        symbol: string
        name: string
        amount: string
        value: string
        change: number
        percentage: number
      }>
      performance: {
        day: number
        week: number
        month: number
        year: number
      }
    }
    trades: Array<{
      id: string
      type: 'buy' | 'sell'
      symbol: string
      amount: string
      price: string
      value: string
      timestamp: string
      profitLoss?: string
    }>
    analysis: Array<{
      id: string
      title: string
      content: string
      category: 'market' | 'technical' | 'fundamental' | 'news'
      sentiment: 'bullish' | 'bearish' | 'neutral'
      views: number
      likes: number
      comments: number
      publishedAt: string
      featured: boolean
    }>
    achievements: Array<{
      id: string
      title: string
      description: string
      icon: string
      date: string
    }>
    newsletter: {
      title: string
      description: string
      subscribers: number
      price: string
    }
    premiumContent: Array<{
      id: string
      title: string
      description: string
      price: string
      category: 'signal' | 'analysis' | 'course' | 'consultation'
      subscribers: number
    }>
    stats: {
      totalViews: number
      avgEngagement: number
      postFrequency: string
      accuracy: number
    }
  }
}

export default function CryptoInfluencer({ data }: CryptoInfluencerProps) {
  const [selectedTab, setSelectedTab] = useState<'portfolio' | 'analysis' | 'trades' | 'premium'>('portfolio')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set())
  const [bookmarkedPosts, setBookmarkedPosts] = useState<Set<string>>(new Set())

  const toggleLike = (postId: string) => {
    const newLikedPosts = new Set(likedPosts)
    if (newLikedPosts.has(postId)) {
      newLikedPosts.delete(postId)
    } else {
      newLikedPosts.add(postId)
    }
    setLikedPosts(newLikedPosts)
  }

  const toggleBookmark = (postId: string) => {
    const newBookmarkedPosts = new Set(bookmarkedPosts)
    if (newBookmarkedPosts.has(postId)) {
      newBookmarkedPosts.delete(postId)
    } else {
      newBookmarkedPosts.add(postId)
    }
    setBookmarkedPosts(newBookmarkedPosts)
  }

  const filteredAnalysis = selectedCategory === 'all' 
    ? data.analysis 
    : data.analysis.filter(post => post.category === selectedCategory)

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'bullish': return 'bg-green-500/20 text-green-400 border-green-400/30'
      case 'bearish': return 'bg-red-500/20 text-red-400 border-red-400/30'
      case 'neutral': return 'bg-gray-500/20 text-gray-400 border-gray-400/30'
      default: return 'bg-gray-500/20 text-gray-400 border-gray-400/30'
    }
  }

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case 'bullish': return <TrendingUp className="w-4 h-4" />
      case 'bearish': return <TrendingDown className="w-4 h-4" />
      case 'neutral': return <BarChart3 className="w-4 h-4" />
      default: return <BarChart3 className="w-4 h-4" />
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'market': return 'bg-blue-500/20 text-blue-400'
      case 'technical': return 'bg-purple-500/20 text-purple-400'
      case 'fundamental': return 'bg-green-500/20 text-green-400'
      case 'news': return 'bg-orange-500/20 text-orange-400'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-orange-900 text-white">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 
                  className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  @{data.handle}
                </h1>
                <p className="text-orange-200 text-sm">Crypto Influencer & Trader</p>
              </div>
            </div>
            
            <nav className="hidden md:flex space-x-8">
              <button
                onClick={() => setSelectedTab('portfolio')}
                className={`transition-colors ${selectedTab === 'portfolio' ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                Portfolio
              </button>
              <button
                onClick={() => setSelectedTab('analysis')}
                className={`transition-colors ${selectedTab === 'analysis' ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                Analysis
              </button>
              <button
                onClick={() => setSelectedTab('trades')}
                className={`transition-colors ${selectedTab === 'trades' ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                Trades
              </button>
              <button
                onClick={() => setSelectedTab('premium')}
                className={`transition-colors ${selectedTab === 'premium' ? 'text-white' : 'text-white/80 hover:text-white'}`}
              >
                Premium
              </button>
            </nav>
            
            <div className="flex items-center space-x-4">
              <button className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors">
                Follow
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Profile Hero */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Profile Info */}
            <div className="lg:w-1/3">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="text-center mb-6">
                  <div className="w-24 h-24 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-12 h-12 text-white" />
                  </div>
                  
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <h2 
                      className="text-2xl font-bold"
                      style={{ fontFamily: 'Georgia, serif' }}
                    >
                      {data.influencerName}
                    </h2>
                    {data.verified && (
                      <div className="bg-gradient-to-r from-gold-400 to-gold-600 rounded-full p-1">
                        <Star className="w-4 h-4 text-white fill-current" />
                      </div>
                    )}
                  </div>
                  
                  <p className="text-orange-300 text-lg">@{data.handle}</p>
                  <p className="text-white/80 mt-4">{data.bio}</p>
                </div>

                {/* Followers */}
                <div className="flex justify-between items-center mb-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">{data.followers.toLocaleString()}</div>
                    <div className="text-orange-300 text-sm">Followers</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">{data.following.toLocaleString()}</div>
                    <div className="text-orange-300 text-sm">Following</div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="flex justify-center gap-4 mb-6">
                  {Object.entries(data.socialLinks).map(([platform, url]) => (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white/10 hover:bg-white/20 p-3 rounded-lg transition-colors"
                    >
                      <span className="text-xs text-orange-300">{platform}</span>
                    </a>
                  ))}
                </div>

                {/* Stats */}
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-white/80">Total Views</span>
                    <span className="text-white font-semibold">{data.stats.totalViews.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/80">Engagement</span>
                    <span className="text-white font-semibold">{data.stats.avgEngagement}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/80">Accuracy</span>
                    <span className="text-green-400 font-semibold">{data.stats.accuracy}%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Portfolio Overview */}
            <div className="lg:w-2/3">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <h3 
                  className="text-2xl font-bold mb-6"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  Portfolio Overview
                </h3>
                
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-4xl font-bold text-white">{data.portfolio.totalValue}</div>
                    <div className={`flex items-center gap-2 text-lg ${
                      data.portfolio.dayChange >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {data.portfolio.dayChange >= 0 ? <ArrowUpRight className="w-5 h-5" /> : <ArrowDownRight className="w-5 h-5" />}
                      {data.portfolio.dayChange >= 0 ? '+' : ''}{data.portfolio.dayChangeValue}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-sm text-white/80">24h Change</div>
                    <div className={`text-lg font-bold ${
                      data.portfolio.dayChange >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {data.portfolio.dayChange >= 0 ? '+' : ''}{data.portfolio.dayChange.toFixed(2)}%
                    </div>
                  </div>
                </div>

                {/* Performance Chart Placeholder */}
                <div className="bg-white/5 rounded-lg p-6 mb-6">
                  <h4 className="text-lg font-semibold text-white mb-4">Performance</h4>
                  <div className="grid grid-cols-4 gap-4">
                    {Object.entries(data.portfolio.performance).map(([period, value]) => (
                      <div key={period} className="text-center">
                        <div className={`text-lg font-bold ${
                          value >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {value >= 0 ? '+' : ''}{value.toFixed(1)}%
                        </div>
                        <div className="text-white/60 text-sm capitalize">{period}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Holdings */}
                <div>
                  <h4 className="text-lg font-semibold text-white mb-4">Top Holdings</h4>
                  <div className="space-y-3">
                    {data.portfolio.topHoldings.map((holding, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">{holding.symbol.charAt(0)}</span>
                          </div>
                          <div>
                            <div className="font-semibold text-white">{holding.symbol}</div>
                            <div className="text-white/60 text-sm">{holding.name}</div>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className="font-bold text-white">{holding.value}</div>
                          <div className={`text-sm ${
                            holding.change >= 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {holding.change >= 0 ? '+' : ''}{holding.change.toFixed(2)}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Tabs */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Portfolio Tab */}
          {selectedTab === 'portfolio' && (
            <div>
              <h2 
                className="text-3xl font-bold mb-8"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Portfolio Details
              </h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Holdings */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-6">All Holdings</h3>
                  <div className="space-y-4">
                    {data.portfolio.topHoldings.map((holding, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold">{holding.symbol.charAt(0)}</span>
                          </div>
                          <div>
                            <div className="font-semibold text-white">{holding.symbol}</div>
                            <div className="text-white/60 text-sm">{holding.amount}</div>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className="font-bold text-white">{holding.value}</div>
                          <div className={`text-sm ${
                            holding.change >= 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {holding.change >= 0 ? '+' : ''}{holding.change.toFixed(2)}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Achievements */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-6">Achievements</h3>
                  <div className="space-y-4">
                    {data.achievements.map((achievement) => (
                      <div key={achievement.id} className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
                        <div className="w-10 h-10 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center">
                          <Award className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="font-semibold text-white">{achievement.title}</div>
                          <div className="text-white/60 text-sm">{achievement.description}</div>
                          <div className="text-orange-300 text-xs">{achievement.date}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Analysis Tab */}
          {selectedTab === 'analysis' && (
            <div>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <h2 
                  className="text-3xl font-bold"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  Market Analysis
                </h2>
                
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-orange-400"
                >
                  <option value="all">All Categories</option>
                  <option value="market">Market Analysis</option>
                  <option value="technical">Technical Analysis</option>
                  <option value="fundamental">Fundamental Analysis</option>
                  <option value="news">News & Updates</option>
                </select>
              </div>

              <div className="space-y-6">
                {filteredAnalysis.map((post) => (
                  <div
                    key={post.id}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 hover:border-orange-400/50 transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getSentimentColor(post.sentiment)}`}>
                          {getSentimentIcon(post.sentiment)}
                          <span className="ml-1">{post.sentiment.charAt(0).toUpperCase() + post.sentiment.slice(1)}</span>
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getCategoryColor(post.category)}`}>
                          {post.category.charAt(0).toUpperCase() + post.category.slice(1)}
                        </span>
                        {post.featured && (
                          <span className="bg-gold-500/20 text-gold-400 px-2 py-1 rounded text-xs font-semibold">
                            Featured
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleBookmark(post.id)}
                          className={`p-2 rounded-lg transition-colors ${
                            bookmarkedPosts.has(post.id)
                              ? 'bg-orange-500 text-white'
                              : 'bg-white/10 text-white/70 hover:bg-white/20'
                          }`}
                        >
                          <Bookmark className="w-4 h-4" />
                        </button>
                        <button className="p-2 bg-white/10 text-white/70 hover:bg-white/20 rounded-lg transition-colors">
                          <Share2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-white mb-4">{post.title}</h3>
                    <p className="text-white/80 mb-6 text-lg leading-relaxed">{post.content}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6 text-white/60">
                        <span className="flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          {post.views.toLocaleString()}
                        </span>
                        <button
                          onClick={() => toggleLike(post.id)}
                          className={`flex items-center gap-2 transition-colors ${
                            likedPosts.has(post.id) ? 'text-red-400' : 'text-white/60 hover:text-red-400'
                          }`}
                        >
                          <TrendingUp className={`w-4 h-4 ${likedPosts.has(post.id) ? 'fill-current' : ''}`} />
                          {post.likes + (likedPosts.has(post.id) ? 1 : 0)}
                        </button>
                        <span className="flex items-center gap-2">
                          <MessageCircle className="w-4 h-4" />
                          {post.comments}
                        </span>
                        <span className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {post.publishedAt}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trades Tab */}
          {selectedTab === 'trades' && (
            <div>
              <h2 
                className="text-3xl font-bold mb-8"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Recent Trades
              </h2>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-6 py-4 text-left text-white/80 font-semibold">Type</th>
                        <th className="px-6 py-4 text-left text-white/80 font-semibold">Symbol</th>
                        <th className="px-6 py-4 text-left text-white/80 font-semibold">Amount</th>
                        <th className="px-6 py-4 text-left text-white/80 font-semibold">Price</th>
                        <th className="px-6 py-4 text-left text-white/80 font-semibold">Value</th>
                        <th className="px-6 py-4 text-left text-white/80 font-semibold">P&L</th>
                        <th className="px-6 py-4 text-left text-white/80 font-semibold">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.trades.map((trade) => (
                        <tr key={trade.id} className="border-t border-white/10">
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              trade.type === 'buy' 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {trade.type.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-6 py-4 font-semibold text-white">{trade.symbol}</td>
                          <td className="px-6 py-4 text-white">{trade.amount}</td>
                          <td className="px-6 py-4 text-white">{trade.price}</td>
                          <td className="px-6 py-4 text-white">{trade.value}</td>
                          <td className="px-6 py-4">
                            {trade.profitLoss && (
                              <span className={`font-semibold ${
                                trade.profitLoss.startsWith('+') ? 'text-green-400' : 'text-red-400'
                              }`}>
                                {trade.profitLoss}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-white/60">{trade.timestamp}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Premium Tab */}
          {selectedTab === 'premium' && (
            <div>
              <h2 
                className="text-3xl font-bold mb-8"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Premium Content
              </h2>
              
              {/* Newsletter */}
              <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 backdrop-blur-sm rounded-xl p-8 border border-orange-400/30 mb-8">
                <h3 className="text-2xl font-bold text-white mb-4">{data.newsletter.title}</h3>
                <p className="text-orange-200 mb-6">{data.newsletter.description}</p>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-bold text-white">{data.newsletter.price}</div>
                    <div className="text-orange-300 text-sm">{data.newsletter.subscribers.toLocaleString()} subscribers</div>
                  </div>
                  <button className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
                    Subscribe
                  </button>
                </div>
              </div>

              {/* Premium Content Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {data.premiumContent.map((content) => (
                  <div
                    key={content.id}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:border-orange-400/50 transition-all duration-300"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        content.category === 'signal' ? 'bg-green-500/20 text-green-400' :
                        content.category === 'analysis' ? 'bg-blue-500/20 text-blue-400' :
                        content.category === 'course' ? 'bg-purple-500/20 text-purple-400' :
                        'bg-orange-500/20 text-orange-400'
                      }`}>
                        {content.category.charAt(0).toUpperCase() + content.category.slice(1)}
                      </span>
                      <span className="text-white/60 text-sm">{content.subscribers} subscribers</span>
                    </div>
                    
                    <h4 className="text-xl font-bold text-white mb-3">{content.title}</h4>
                    <p className="text-white/80 mb-4">{content.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-orange-400">{content.price}</span>
                      <button className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors">
                        Access
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/50 backdrop-blur-sm border-t border-white/10 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center gap-6 mb-6">
            <a href="#" className="text-gray-400 hover:text-orange-400 transition-colors">Twitter</a>
            <a href="#" className="text-gray-400 hover:text-orange-400 transition-colors">YouTube</a>
            <a href="#" className="text-gray-400 hover:text-orange-400 transition-colors">Instagram</a>
            <a href="#" className="text-gray-400 hover:text-orange-400 transition-colors">TikTok</a>
          </div>
          <p className="text-gray-400">
            &copy; 2024 @{data.handle}. Follow for crypto insights and trading signals.
          </p>
        </div>
      </footer>
    </div>
  )
}
